import { useMemo } from "react"
import toast from "react-hot-toast"
import {
  BusinessTypeEnum,
  CurrentSubscriptionFragment,
} from "~/__generated__/graphql"
import { useCurrentUser } from "~/auth/CurrentUserContext"
import { subscriptionBillingCycleName } from "~/common/enums"
import { useSafeMutation } from "~/common/useSafeMutation"
import { useSubscriptionTiers } from "~/common/useSubscriptionTiers"
import { Alert } from "~/shadcn/ui/alert"
import { Button } from "~/shadcn/ui/button"
import Text from "~/ui/typography"
import { formatDate } from "~/util/dates"
import { flushEvents, trackEvent } from "~/util/tracking"
import { SUBSCRIPTION_PORTAL_SESSION_CREATE_MUTATION } from "../api"
import { Label } from "./Label"

export const CurrentSubscription = ({
  subscription,
}: {
  subscription: CurrentSubscriptionFragment
}) => {
  const currentUser = useCurrentUser()
  const [runCreateSession] = useSafeMutation(
    SUBSCRIPTION_PORTAL_SESSION_CREATE_MUTATION
  )
  const { subscriptionTiers } = useSubscriptionTiers(
    currentUser.agency?.businessType || BusinessTypeEnum.Talent
  )

  const redirectToPortalSession = async () => {
    const { data } = await runCreateSession({ variables: { input: {} } })
    if (data?.portalSessionCreate?.sessionUrl) {
      trackEvent("open_billing_portal", {})
      await flushEvents()
      window.location.href = data.portalSessionCreate.sessionUrl
    } else {
      toast.error(
        "We couldn’t open the billing portal. Please try again, or contact support@zanoplatform.com."
      )
    }
  }

  const currentTier = subscription.tier

  const highestInapplicableTier = useMemo(() => {
    return subscriptionTiers
      .filter(
        (tier) =>
          tier.billingCycle === currentTier?.billingCycle &&
          ((tier.remainingInfluencerQuota &&
            tier.remainingInfluencerQuota < 0) ||
            (tier.remainingUserQuota && tier.remainingUserQuota < 0))
      )
      .pop()
  }, [subscriptionTiers, currentTier])

  // A subscription with no tier isn't a usable plan — e.g. the Stripe
  // subscription was cancelled/removed and only an orphaned record remains, or a
  // price mid-migration maps to no tier. There's nothing to manage in the
  // billing portal, so show a clear "no active subscription" message instead of
  // a button that silently fails.
  if (!currentTier) {
    return (
      <Text as="p" variant="body2">
        You don’t have an active subscription. If you think this is a mistake,
        contact{" "}
        <a
          href="mailto:support@zanoplatform.com"
          className="text-blueDark underline-offset-4 hover:underline"
        >
          support@zanoplatform.com
        </a>
        .
      </Text>
    )
  }

  return (
    <>
      <Text as="p" variant="body2" className="mb-3">
        You are currently subscribed to the <b>{currentTier.name}</b> plan.
        Click “manage my plan” to change your plan, update billing information,
        view and download receipts, and more.
      </Text>
      <div className="flex flex-row gap-3">
        <Button variant="secondary" onClick={redirectToPortalSession}>
          Manage My Plan
        </Button>
        {highestInapplicableTier && (
          <Alert variant="warning">
            Note: If you need to downgrade your plan, first make sure the number
            of users in your team and / or the number of influencers in your
            roster match the plans limit and then downgrade.
          </Alert>
        )}
      </div>
      <div className="grid grid-cols-4 gap-8 mt-8">
        <div>
          <Label>Plan</Label>
          <div>
            {currentTier.name} (
            {subscriptionBillingCycleName(currentTier.billingCycle)})
          </div>
        </div>
        <div>
          <Label>Subscription Start Date</Label>
          <div>{formatDate(subscription.createdAt)}</div>
        </div>
        {subscription.status === "trialing" && (
          <div>
            <Label>Free Trial Period</Label>
            <div>
              {formatDate(subscription.currentPeriodStart)} -{" "}
              {formatDate(subscription.currentPeriodEnd)}
            </div>
          </div>
        )}
        <div>
          <Label>
            {subscription.cancelAtPeriodEnd
              ? "Subscription End"
              : "Next Payment"}
          </Label>
          <div>{formatDate(subscription.currentPeriodEnd)}</div>
        </div>
      </div>
    </>
  )
}
